import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Loader2, ArrowRight, Share2 } from "lucide-react";
import { toast } from "sonner";
import { ARCHETYPE_META } from "@/data/quiz";
import { useArchetypeCopy, getComboFrom, getShadowFrom } from "@/data/results";
import SpiderChart from "@/components/SpiderChart";
import MapaCompletoSection from "@/components/MapaCompletoSection";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ResultPage() {
  const { id } = useParams();
  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { copy: archetypeCopy } = useArchetypeCopy();

  useEffect(() => {
    let mounted = true;
    axios
      .get(`${API}/lead/${id}`)
      .then(({ data }) => {
        if (mounted) {
          setLead(data);
          setLoading(false);
        }
      })
      .catch(() => {
        if (mounted) {
          setError("Resultado no encontrado");
          setLoading(false);
        }
      });
    return () => {
      mounted = false;
    };
  }, [id]);

  const share = async () => {
    const base = process.env.REACT_APP_BACKEND_URL;
    const url = `${base}/api/share/${id}`;
    try {
      if (navigator.share) {
        await navigator.share({
          title: lead ? `${lead.name} es ${ARCHETYPE_META[lead.archetype]?.name}` : "PLAY YOUR GAME™",
          text: "Descubrí mi forma de jugar bajo presión.",
          url,
        });
      } else {
        await navigator.clipboard.writeText(url);
        toast.success("Enlace copiado");
      }
    } catch (e) {
      /* user cancelled */
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center text-white">
        <Loader2 className="w-6 h-6 animate-spin text-[#29BC9B]" />
      </div>
    );
  }

  if (error || !lead) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center text-white px-6 text-center">
        <div>
          <h1 className="font-display text-3xl mb-3">Resultado no encontrado</h1>
          <a href="/" className="text-[#29BC9B] underline text-sm">
            Volver a la página principal
          </a>
        </div>
      </div>
    );
  }

  const dominant = ARCHETYPE_META[lead.archetype];
  const secondary = ARCHETYPE_META[lead.secondary];
  const combo = getComboFrom(archetypeCopy, lead.archetype, lead.secondary);
  const shadow = getShadowFrom(archetypeCopy, lead.archetype);

  return (
    <div className="min-h-screen bg-[#050505] text-white" data-testid="result-page">
      <header className="border-b border-white/8 px-6 md:px-12 h-16 flex items-center justify-between">
        <a href="/" className="font-display text-sm tracking-tighter text-white/80">
          PLAY YOUR GAME™
        </a>
        <button onClick={share} className="btn-ghost !py-2 !px-4 !text-[10px]">
          <Share2 className="w-3 h-3" /> Compartir
        </button>
      </header>

      <main className="max-w-3xl mx-auto px-6 md:px-12 py-16 md:py-24 space-y-12">
        {/* Header */}
        <div className="text-center space-y-5">
          <div className="text-[10px] uppercase tracking-[0.4em] text-[#29BC9B]">
            Tu forma de jugar dominante
          </div>
          <div className="flex justify-center">
            <div
              className="w-28 h-28 rounded-full flex items-center justify-center"
              style={{
                background: `radial-gradient(circle, ${dominant.color}22 0%, transparent 70%)`,
                border: `1px solid ${dominant.color}40`,
              }}
            >
              <img
                src={dominant.img}
                alt={dominant.name}
                className="w-16 h-16 object-contain"
                style={{ filter: "brightness(0) invert(1)" }}
              />
            </div>
          </div>
          <h1 className="font-display text-5xl md:text-7xl">
            {lead.name}, eres {dominant.name}
          </h1>
          <p className="text-white/60 italic text-lg">{dominant.tagline}</p>
        </div>

        {secondary && (
          <div className="flex items-center justify-center gap-3 text-sm">
            <span className="text-white/40">Secundario:</span>
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/10 bg-white/[0.02]">
              <img
                src={secondary.img}
                alt={secondary.name}
                className="w-4 h-4 object-contain"
                style={{ filter: "brightness(0) invert(1)" }}
              />
              <span className="font-display tracking-wider">{secondary.name}</span>
            </div>
          </div>
        )}

        {combo && (
          <div className="p-6 md:p-8 rounded-sm border border-[#29BC9B]/30 bg-[#29BC9B]/[0.04]">
            <div className="text-[10px] uppercase tracking-[0.35em] text-[#29BC9B] mb-3">
              Tu ventaja estratégica
            </div>
            <h3 className="font-display text-2xl md:text-3xl mb-4">{combo.title}</h3>
            <p className="text-white/80 leading-relaxed">{combo.body}</p>
          </div>
        )}

        {shadow && (
          <div className="p-6 md:p-8 rounded-sm border border-white/10 bg-[#0A0A0A]">
            <div className="text-[10px] uppercase tracking-[0.35em] text-white/50 mb-3 flex items-center gap-2">
              <span className="w-1 h-1 rounded-full bg-red-400/70" /> Tu modo sombra
            </div>
            <h3 className="font-display text-xl md:text-2xl mb-5">
              Cuando tu {dominant.name} entra en modo sombra
            </h3>
            <ul className="space-y-2 mb-5">
              {shadow.bullets.map((b, i) => (
                <li key={i} className="flex gap-3 text-white/80 text-sm">
                  <span className="text-white/30">—</span>
                  <span>{b}</span>
                </li>
              ))}
            </ul>
            <p className="text-sm italic">
              <span className="text-[#29BC9B] mr-1">→</span>
              {shadow.danger}
            </p>
          </div>
        )}

        {lead.scores && (
          <div className="space-y-4">
            <div className="text-center">
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#29BC9B]">
                Tu reparto de puntos
              </div>
              <p className="text-xs text-white/40 mt-1">
                Distribución de tu comportamiento en acción, análisis, visión y conexión
              </p>
            </div>
            <SpiderChart scores={lead.scores} />
          </div>
        )}

        <div className="text-center pt-6">
          <a href="/" className="btn-teal" data-testid="result-cta">
            Hacer el test <ArrowRight className="w-4 h-4" />
          </a>
        </div>
      </main>

      {/* Premium "Mapa Completo" intent capture — below free result */}
      <div className="max-w-5xl mx-auto pb-24">
        <MapaCompletoSection lead={lead} source="resultado_test" />
      </div>

      <footer className="border-t border-white/5 px-6 md:px-12 py-10 text-center text-[10px] uppercase tracking-[0.3em] text-white/30">
        © PLAY YOUR GAME™ — Evolución bajo presión
      </footer>
    </div>
  );
}
